import React, { useState } from "react";
import { supabase } from "../lib/supabase";
import { useLanguage, t } from "../lib/i18n";

const SERVICES = [
  { id:"auth",     emoji:"🛡️", name:"Authentication",     price:300,  desc:"Expert examination and certification of your vintage Indian watch. Certificate issued with detailed report." },
  { id:"full",     emoji:"⚙️", name:"Full Service",        price:1500, desc:"Complete movement overhaul — disassembly, ultrasonic cleaning, new lubricants, gaskets, timing regulation." },
  { id:"movement", emoji:"🔬", name:"Movement Service",    price:2500, desc:"Deep-clean movement, diagnose faults, replace worn parts, regulation to ±10 sec/day." },
  { id:"crystal",  emoji:"💎", name:"Crystal Replacement", price:800,  desc:"Source and fit the correct acrylic or mineral crystal for your specific reference." },
  { id:"crown",    emoji:"🔩", name:"Crown / Stem Repair", price:600,  desc:"Replace a damaged, worn or missing crown and stem. Improves water resistance and winding feel." },
  { id:"polish",   emoji:"✨", name:"Case & Dial Polish",  price:500,  desc:"Careful restoration of case finish. We match the original brushed or polished surfaces." },
];

const COURIER_STEPS = [
  { n:"01", t:"Book your service", b:"Submit the form below. We'll confirm via WhatsApp within 24 hours with packing instructions and our Jaipur address." },
  { n:"02", t:"Pack & ship",       b:"Wrap your watch in bubble wrap + a rigid box. Use any insured courier (Blue Dart, Shiprocket, DTDC). Send us the tracking number." },
  { n:"03", t:"We service it",     b:"Our watchmaker examines on arrival, services, and sends you a condition report and estimated completion date." },
  { n:"04", t:"We ship it back",   b:"Once complete, we ship back insured. You pay service charge + return shipping on delivery. No advance." },
];

const inputStyle = { padding:"11px 14px", borderRadius:10, border:"1.5px solid var(--bd)", fontSize:14, fontFamily:"'DM Sans',sans-serif", background:"var(--bg)", outline:"none", width:"100%", boxSizing:"border-box" };
const labelStyle = { display:"flex", flexDirection:"column", gap:5 };
const labelTextStyle = { fontFamily:"'DM Mono',monospace", fontSize:10, color:"var(--ink3)", letterSpacing:"0.08em" };

export function ServicesView({ session, openAuth, toast, requests, saveRequests }) {
  const { lang } = useLanguage();
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState({ name:"", email:"", phone:"", brand:"", model:"", notes:"", delivery:"courier" });
  const [submitted, setSubmitted]   = useState(false);
  const [partnerTab, setPartnerTab] = useState("watchmaker");
  const [partnerForm, setPartnerForm] = useState({ name:"", city:"", specialty:"", instagram:"", email:"", phone:"", message:"" });
  const [partnerSent, setPartnerSent] = useState(false);

  const u = session?.user;

  function openService(svc) {
    if (!u) { openAuth?.(); return; }
    setSelected(svc);
    setForm({ name:u.name||"", email:u.email||"", phone:u.phone||"", brand:"", model:"", notes:"", delivery:"courier" });
    setSubmitted(false);
  }

  async function submit() {
    if (!form.name||!form.email||!form.phone||!form.brand) { toast?.("Fill in all required fields"); return; }
    const id = "svc-" + Date.now();
    const req = {
      id, type:"service", serviceType:selected.id,
      watch:`${form.brand}${form.model?" "+form.model:""}`,
      name:form.name, email:form.email, phone:form.phone,
      userId:u?.id||null, status:"Received", amount:selected.price,
      notes:form.notes, delivery:form.delivery,
      createdAt:new Date().toISOString(),
      updates:[{ note:`Service request: ${selected.name} · ${form.delivery==="courier"?"Courier in/out":"Drop-off at Jaipur"}`, at:new Date().toISOString() }],
    };
    try {
      await supabase.from("requests").upsert(req);
      setSubmitted(true);
      toast?.("Request received!");
    } catch {
      // fall back to local
      const updated = [...(requests||[]), req];
      await saveRequests?.(updated);
      setSubmitted(true);
      toast?.("Request received!");
    }
  }

  async function submitPartner() {
    if (!partnerForm.name||!partnerForm.email||!partnerForm.city) { toast?.("Name, email and city are required"); return; }
    const id = "partner-" + Date.now();
    try {
      await supabase.from("requests").upsert({
        id, type:"partner_application", partnerType:partnerTab,
        name:partnerForm.name, email:partnerForm.email, phone:partnerForm.phone,
        notes:JSON.stringify(partnerForm), status:"Received",
        createdAt:new Date().toISOString(),
        updates:[{ note:`Partner application: ${partnerTab}`, at:new Date().toISOString() }],
      });
    } catch {}
    setPartnerSent(true);
    toast?.("Application sent!");
  }

  // ── Service booking form ────────────────────────────────────────────────────
  if (selected) {
    return (
      <div style={{maxWidth:540,margin:"0 auto"}}>
        <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:"var(--ink3)",fontSize:13,cursor:"pointer",marginBottom:24,padding:0,fontFamily:"'DM Mono',monospace",letterSpacing:"0.06em"}}>← BACK TO SERVICES</button>

        {submitted ? (
          <div className="fade-in">
            <div style={{background:"#0C1A0E",borderRadius:20,padding:"40px 32px",marginBottom:24,textAlign:"center"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,color:"#6EE7B7",marginBottom:12}}>✓</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,color:"#fff",marginBottom:8}}>{t("svc_success_h",lang)}</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.45)",lineHeight:1.75}}>{t("svc_success_b",lang)}</div>
            </div>

            {form.delivery==="courier" && (
              <div style={{marginBottom:24}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.2em",color:"var(--ink3)",marginBottom:16,textTransform:"uppercase"}}>What happens next</div>
                {COURIER_STEPS.map(s=>(
                  <div key={s.n} style={{display:"flex",gap:16,marginBottom:16,alignItems:"flex-start"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:"var(--bg3)",lineHeight:1,flexShrink:0,width:28}}>{s.n}</div>
                    <div>
                      <div style={{fontSize:14,fontWeight:600,marginBottom:3}}>{s.t}</div>
                      <div style={{fontSize:13,color:"var(--ink3)",lineHeight:1.65}}>{s.b}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{background:"var(--bg2)",borderRadius:12,padding:"14px 16px",marginBottom:20}}>
              <div style={{fontSize:13,color:"var(--ink2)",lineHeight:1.65}}>
                <strong>Payment:</strong> Service charge of ₹{selected.price.toLocaleString("en-IN")} is due <em>on completion</em>. No advance required. We accept UPI / bank transfer.
              </div>
            </div>
            <button className="btn-ghost" style={{width:"100%",padding:"12px 0"}} onClick={()=>setSelected(null)}>Back to Services</button>
          </div>
        ) : (
          <div className="fade-in">
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
              <div style={{fontSize:34}}>{selected.emoji}</div>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,lineHeight:1.1}}>{selected.name}</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--gold)",marginTop:3}}>₹{selected.price.toLocaleString("en-IN")} · pay on completion</div>
              </div>
            </div>
            <p style={{fontSize:14,color:"var(--ink3)",lineHeight:1.75,marginBottom:28}}>{selected.desc}</p>

            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Delivery method */}
              <div>
                <div style={{...labelTextStyle,marginBottom:8}}>HOW ARE YOU SENDING IT?</div>
                <div style={{display:"flex",gap:8}}>
                  {[["courier","📦 Courier in / out"],["dropin","🏪 Drop off in Jaipur"]].map(([val,lbl])=>(
                    <button key={val} onClick={()=>setForm(f=>({...f,delivery:val}))}
                      style={{flex:1,padding:"10px 12px",borderRadius:10,border:`1.5px solid ${form.delivery===val?"var(--ink)":"var(--bd)"}`,background:form.delivery===val?"#111118":"transparent",color:form.delivery===val?"#fff":"var(--ink2)",cursor:"pointer",fontSize:13,fontFamily:"'DM Sans',sans-serif",transition:"all .15s"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {[["Name *","name","text"],["Email *","email","email"],["WhatsApp / Phone *","phone","tel"],["Watch Brand *","brand","text"],["Watch Model / Reference","model","text"]].map(([label,field,type])=>(
                <label key={field} style={labelStyle}>
                  <span style={labelTextStyle}>{label.toUpperCase()}</span>
                  <input type={type} value={form[field]} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))} style={inputStyle}/>
                </label>
              ))}

              <label style={labelStyle}>
                <span style={labelTextStyle}>DESCRIBE THE ISSUE OR WORK NEEDED</span>
                <textarea rows={3} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,resize:"vertical"}}/>
              </label>

              <div style={{background:"var(--bg2)",borderRadius:12,padding:"14px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:14,color:"var(--ink3)"}}>Service charge</span>
                  <span style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700}}>₹{selected.price.toLocaleString("en-IN")}</span>
                </div>
                <div style={{fontSize:11,color:"var(--ink3)"}}>No advance · Pay on completion via UPI</div>
              </div>

              <button className="btn-ink" style={{padding:"14px 0",fontSize:15}} onClick={submit}>{t("svc_submit",lang)}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main services page ──────────────────────────────────────────────────────
  return (
    <div className="fade-in">

      {/* Hero */}
      <div style={{background:"#0C1208",borderRadius:20,padding:"52px 48px 48px",marginBottom:52,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse 50% 70% at 80% 50%,rgba(5,100,60,.2) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <div style={{position:"relative",zIndex:1,maxWidth:560}}>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.28em",color:"rgba(110,231,183,.6)",textTransform:"uppercase",marginBottom:20}}>Jaipur Royal Atelier · Est. 2026</div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:44,fontWeight:700,color:"#fff",lineHeight:1.1,marginBottom:16,letterSpacing:"-0.02em"}}>
            {t("svc_head2",lang)}
          </h1>
          <p style={{fontSize:14,color:"rgba(255,255,255,.4)",lineHeight:1.85,maxWidth:440,marginBottom:32}}>
            In-house watchmaker in Jaipur. HMT, Allwyn, Ricoh, and other vintage Indian movements. Nationwide service via insured courier.
          </p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {[["🔒","No advance payment"],["📦","Courier in / courier out"],["🏆","Certificate on authentication"]].map(([ic,t])=>(
              <span key={t} style={{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",borderRadius:20,padding:"6px 14px",fontSize:12,color:"rgba(255,255,255,.55)",fontFamily:"'DM Mono',monospace",letterSpacing:"0.04em"}}>{ic} {t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Courier flow */}
      <div style={{marginBottom:52}}>
        <span className="sl">How courier service works</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:28}}>Send it. We fix it. We send it back.</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:1,background:"var(--bd)",borderRadius:16,overflow:"hidden"}}>
          {COURIER_STEPS.map(s=>(
            <div key={s.n} style={{background:"var(--bg)",padding:"24px 22px"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:40,fontWeight:700,color:"var(--bg3)",lineHeight:1,marginBottom:14,letterSpacing:"-0.03em"}}>{s.n}</div>
              <div style={{fontSize:15,fontWeight:600,marginBottom:8,lineHeight:1.2}}>{s.t}</div>
              <div style={{fontSize:13,color:"var(--ink3)",lineHeight:1.7}}>{s.b}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Service grid */}
      <div style={{marginBottom:52}}>
        <span className="sl">Services & pricing</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:24}}>What we offer.</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
          {SERVICES.map(svc=>(
            <div key={svc.id} onClick={()=>openService(svc)} className="card"
              style={{cursor:"pointer",display:"flex",flexDirection:"column",gap:10}}
              onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 12px 36px rgba(0,0,0,.12)";}}
              onMouseOut={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{fontSize:28}}>{svc.emoji}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"var(--gold)"}}>₹{svc.price.toLocaleString("en-IN")}</div>
              </div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,lineHeight:1.2}}>{svc.name}</div>
              <div style={{fontSize:12.5,color:"var(--ink3)",lineHeight:1.65,flex:1}}>{svc.desc}</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em",marginTop:4}}>{t("svc_book",lang).toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Straps & Accessories */}
      <div style={{marginBottom:52}}>
        <span className="sl">Straps & Accessories</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:8}}>For the full look.</h2>
        <p style={{fontSize:14,color:"var(--ink3)",lineHeight:1.75,marginBottom:24,maxWidth:520}}>
          We stock and source straps, crystals, and accessories for vintage Indian watches. Enquire via chat for availability, sizing, and custom orders.
        </p>
        <div style={{background:"var(--bg2)",borderRadius:16,padding:"24px 28px",display:"flex",gap:20,flexWrap:"wrap",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,marginBottom:6}}>Ask about straps & parts</div>
            <div style={{fontSize:13,color:"var(--ink3)",lineHeight:1.65,maxWidth:380}}>
              Nato nylons, leather pads, tropical rubber, mesh bracelets, acrylic crystals, spring bars — we'll source what you need.
            </div>
          </div>
          <button className="btn-ink" style={{padding:"12px 24px",flexShrink:0,whiteSpace:"nowrap"}} onClick={()=>{ if(!u){openAuth?.();return;} window.location.hash="chat"; }}>
            Chat with us →
          </button>
        </div>
      </div>

      {/* Partner section */}
      <div style={{borderTop:"1px solid var(--bd)",paddingTop:48}}>
        <span className="sl">Join the network</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:8}}>Are you a watchmaker or vendor?</h2>
        <p style={{fontSize:14,color:"var(--ink3)",lineHeight:1.75,marginBottom:28,maxWidth:520}}>
          We're building India's first curated service network for vintage watches. Partner with us — take service jobs through the platform or sell your straps and accessories to the community.
        </p>

        {/* Partner type tabs */}
        <div style={{display:"flex",gap:0,marginBottom:24,borderRadius:12,overflow:"hidden",border:"1.5px solid var(--bd)",width:"fit-content"}}>
          {[["watchmaker","⚙️  Watchmaker"],["vendor","🪡  Strap & Accessory Vendor"]].map(([t,l])=>(
            <button key={t} onClick={()=>setPartnerTab(t)}
              style={{padding:"10px 22px",border:"none",background:partnerTab===t?"#111118":"transparent",color:partnerTab===t?"#fff":"var(--ink2)",cursor:"pointer",fontSize:13,fontFamily:"'DM Sans',sans-serif",fontWeight:partnerTab===t?600:400,transition:"all .15s"}}>
              {l}
            </button>
          ))}
        </div>

        {partnerSent ? (
          <div style={{background:"var(--gnbg)",border:"1.5px solid var(--gnbd)",borderRadius:16,padding:"28px 24px",maxWidth:520}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,marginBottom:8,color:"var(--grn)"}}>Application received ✓</div>
            <div style={{fontSize:14,color:"var(--ink3)",lineHeight:1.7}}>We'll review your application and get in touch within 3–5 working days.</div>
          </div>
        ) : (
          <div style={{maxWidth:520,display:"flex",flexDirection:"column",gap:14}}>
            {partnerTab==="watchmaker" ? (
              <>
                <p style={{fontSize:13,color:"var(--ink3)",lineHeight:1.7,margin:0}}>Verified watchmakers can receive service jobs routed by our platform, build a reputation with reviews, and get listed on the THC service network.</p>
                {[["Your name *","name","text"],["City *","city","text"],["Specialisations (HMT, Rolex, etc.)","specialty","text"],["Email *","email","email"],["Phone / WhatsApp","phone","tel"]].map(([label,field,type])=>(
                  <label key={field} style={labelStyle}>
                    <span style={labelTextStyle}>{label.toUpperCase()}</span>
                    <input type={type} value={partnerForm[field]} onChange={e=>setPartnerForm(f=>({...f,[field]:e.target.value}))} style={inputStyle}/>
                  </label>
                ))}
              </>
            ) : (
              <>
                <p style={{fontSize:13,color:"var(--ink3)",lineHeight:1.7,margin:0}}>Strap makers, crystal suppliers, and accessory vendors can sell through THC. Get a page on the platform, reach collectors directly, and ship via courier.</p>
                {[["Business / brand name *","name","text"],["City *","city","text"],["Instagram handle","instagram","text"],["Email *","email","email"],["Phone / WhatsApp","phone","tel"]].map(([label,field,type])=>(
                  <label key={field} style={labelStyle}>
                    <span style={labelTextStyle}>{label.toUpperCase()}</span>
                    <input type={type} value={partnerForm[field]} onChange={e=>setPartnerForm(f=>({...f,[field]:e.target.value}))} style={inputStyle}/>
                  </label>
                ))}
              </>
            )}
            <label style={labelStyle}>
              <span style={labelTextStyle}>TELL US ABOUT YOUR WORK</span>
              <textarea rows={3} value={partnerForm.message} onChange={e=>setPartnerForm(f=>({...f,message:e.target.value}))} style={{...inputStyle,resize:"vertical"}}/>
            </label>
            <button className="btn-ink" style={{padding:"13px 0",fontSize:14}} onClick={submitPartner}>Apply to join →</button>
          </div>
        )}
      </div>

    </div>
  );
}
